import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { collection, getDocs, where, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Card, Button } from '../UI';
import {
  BookOpen, Plus, FolderPlus, Globe, Lock, Trash2, Edit2,
  X, ArrowLeft, Search, Check, ChefHat, Clock, Users, Star, FileEdit,
  Type, AlignLeft, Heading1, Heading2, BookMarked, Image as ImageIcon,
  Upload, ChevronUp, ChevronDown, Leaf,
} from 'lucide-react';
import { useCookbooks } from '../../hooks/useCookbooks';
import { useCareProfiles } from '../../hooks/useCareProfiles';
import { getRecipeImageURL } from '../../hooks/useRecipes';
import { uploadImageToR2 } from '../../services/r2UploadService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DIETARY_TAGS = [
  'Vegan', 'Vegetarian', 'Pescatarian', 'Gluten-Free', 'Dairy-Free',
  'Keto', 'Paleo', 'Low-Carb', 'Nut-Free', 'Halal', 'Kosher', 'High-Protein',
];

// Section types matching iOS CookbookSection.SectionType
const SECTION_TYPES = {
  heading:   'heading',
  subtitle:  'subtitle',
  paragraph: 'paragraph',
  recipe:    'recipe',
};

// ---------------------------------------------------------------------------
// Portal Modal
// ---------------------------------------------------------------------------

const Modal = ({ children, onBackdropClick }) => {
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-lg overflow-y-auto"
      onClick={onBackdropClick}
    >
      <div onClick={e => e.stopPropagation()}>{children}</div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Care Profile Avatar Chip
// ---------------------------------------------------------------------------

const ProfileChip = ({ profile, size = 'sm' }) => {
  const COLORS = ['#2D5F4D', '#8B6F47', '#06B6D4', '#7C3AED', '#D97706', '#DC2626'];
  const bg  = profile.avatarColor || COLORS[(profile.name?.charCodeAt(0) || 0) % COLORS.length];
  const dim = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-caption';
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ring-2 ring-background`}
      style={{ backgroundColor: bg }}
      title={profile.name}
    >
      {(profile.name || '?').slice(0, 1).toUpperCase()}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Status Selector
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: 'published', label: 'Published', description: 'Visible to everyone',       icon: Globe,     activeClass: 'border-accent/30 bg-accent/5',           iconClass: 'bg-accent/15',          iconColor: 'text-accent'        },
  { value: 'draft',     label: 'Draft',     description: 'Saved, not yet published',  icon: FileEdit,  activeClass: 'border-primary/30 bg-primary/5',         iconClass: 'bg-primary/15',         iconColor: 'text-primary'       },
  { value: 'private',   label: 'Private',   description: 'Only you can see this',     icon: Lock,      activeClass: 'border-text-tertiary/30 bg-surface',     iconClass: 'bg-text-tertiary/10',   iconColor: 'text-text-tertiary' },
];

const StatusSelector = ({ value, onChange }) => (
  <div className="space-y-sm">
    {STATUS_OPTIONS.map(opt => {
      const Icon   = opt.icon;
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`w-full flex items-center gap-md p-md rounded-medium border transition-all ${
            active ? opt.activeClass : 'border-text-tertiary/15 bg-surface hover:border-text-tertiary/30'
          }`}
        >
          <div className={`w-8 h-8 rounded-medium flex items-center justify-center flex-shrink-0 ${active ? opt.iconClass : 'bg-text-tertiary/10'}`}>
            <Icon size={16} className={active ? opt.iconColor : 'text-text-tertiary'} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-callout font-semibold text-text-primary">{opt.label}</p>
            <p className="text-caption text-text-secondary">{opt.description}</p>
          </div>
          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            active ? 'border-primary bg-primary' : 'border-text-tertiary/30'
          }`}>
            {active && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
        </button>
      );
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Cookbook Book-Cover Card
// ---------------------------------------------------------------------------

const CookbookCard = ({ cookbook, recipeImages, careProfiles, onClick, onDelete }) => {
  const tagged = careProfiles.filter(p => (cookbook.careProfileIds || []).includes(p.id));
  const count  = cookbook.recipeIds?.length || 0;
  const imgs   = recipeImages || [];

  return (
    <div className="group relative cursor-pointer" onClick={onClick}>
      <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-xl bg-black/15 blur-sm transition-all duration-300 group-hover:translate-x-3 group-hover:translate-y-3 group-hover:blur-md" />

      <div className="relative rounded-xl overflow-hidden border border-black/8 bg-white transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl">

        {/* Cover */}
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/12 via-primary/6 to-accent/8">
          {cookbook.coverImageURL ? (
            <img src={cookbook.coverImageURL} alt={cookbook.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : imgs.length >= 4 ? (
            <div className="grid grid-cols-2 grid-rows-2 w-full h-full gap-px">
              {imgs.slice(0, 4).map((url, i) => (
                <div key={i} className="overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          ) : imgs.length === 3 ? (
            <div className="flex w-full h-full gap-px">
              <div className="flex-1 overflow-hidden"><img src={imgs[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="flex-1 flex flex-col gap-px">
                {imgs.slice(1).map((url, i) => (
                  <div key={i} className="flex-1 overflow-hidden"><img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                ))}
              </div>
            </div>
          ) : imgs.length === 2 ? (
            <div className="flex w-full h-full gap-px">
              {imgs.map((url, i) => <div key={i} className="flex-1 overflow-hidden"><img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>)}
            </div>
          ) : imgs.length === 1 ? (
            <img src={imgs[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-sm">
              <BookOpen size={44} className="text-primary/20" />
              <p className="text-caption text-primary/25 font-medium">No recipes yet</p>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

          {/* Delete btn */}
          <button
            onClick={e => { e.stopPropagation(); onDelete(e); }}
            className="absolute top-sm left-sm w-7 h-7 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-error/80 z-10"
          >
            <Trash2 size={13} className="text-white" />
          </button>

          {/* Status badge */}
          <div className="absolute top-sm right-sm">
            {(() => {
              const s = cookbook.status || (cookbook.isPublic ? 'published' : 'private');
              if (s === 'published') return <span className="px-sm py-xs rounded-full text-[11px] font-semibold flex items-center gap-xs backdrop-blur-sm bg-accent/85 text-white"><Globe size={10} />Published</span>;
              if (s === 'draft')     return <span className="px-sm py-xs rounded-full text-[11px] font-semibold flex items-center gap-xs backdrop-blur-sm bg-black/45 text-white"><FileEdit size={10} />Draft</span>;
              return <span className="px-sm py-xs rounded-full text-[11px] font-semibold flex items-center gap-xs backdrop-blur-sm bg-black/45 text-white"><Lock size={10} />Private</span>;
            })()}
          </div>

          {/* Recipe count */}
          <div className="absolute bottom-sm left-sm">
            <span className="px-sm py-xs rounded-full bg-black/50 backdrop-blur-sm text-white text-[11px] font-semibold flex items-center gap-xs">
              <BookOpen size={10} />
              {count} recipe{count !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-md">
          <h3 className="text-callout font-bold text-text-primary truncate">{cookbook.name}</h3>
          {cookbook.description
            ? <p className="text-caption text-text-secondary line-clamp-2 mt-xs leading-relaxed">{cookbook.description}</p>
            : <p className="text-caption text-text-tertiary/40 italic mt-xs">No description</p>
          }
          {tagged.length > 0 && (
            <div className="flex items-center gap-xs mt-sm">
              <span className="text-[11px] text-text-tertiary">For</span>
              <div className="flex -space-x-1.5">
                {tagged.slice(0, 5).map(p => <ProfileChip key={p.id} profile={p} size="sm" />)}
                {tagged.length > 5 && (
                  <div className="w-6 h-6 rounded-full bg-text-tertiary/15 flex items-center justify-center text-[9px] font-bold text-text-secondary ring-2 ring-background">
                    +{tagged.length - 5}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Spine */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary/50 via-primary/25 to-primary/50 rounded-l-xl" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SectionBlock — renders a single read-only section in the cookbook body
// ---------------------------------------------------------------------------

const SectionBlock = ({ section, recipeMap, onClickRecipe }) => {
  if (section.type === SECTION_TYPES.heading) {
    return (
      <h2 className="text-title-2 font-bold text-text-primary leading-snug">
        {section.text}
      </h2>
    );
  }
  if (section.type === SECTION_TYPES.subtitle) {
    return (
      <h3 className="text-title-3 font-semibold text-text-secondary leading-snug">
        {section.text}
      </h3>
    );
  }
  if (section.type === SECTION_TYPES.paragraph || section.type === 'text') {
    return (
      <p className="text-body text-text-secondary leading-relaxed whitespace-pre-wrap">
        {section.text}
      </p>
    );
  }
  if (section.type === SECTION_TYPES.recipe && section.recipeId) {
    const recipe = recipeMap[section.recipeId];
    if (!recipe) return null;
    const imgURL = getRecipeImageURL(recipe);
    const time   = recipe.totalTimeMinutes || ((recipe.prepTimeMinutes || 0) + (recipe.cookTimeMinutes || 0)) || null;
    return (
      <div
        className="group cursor-pointer rounded-large overflow-hidden bg-surface flex gap-0 hover:shadow-md transition-all"
        style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
        onClick={() => onClickRecipe && onClickRecipe(recipe)}
      >
        <div className="w-28 flex-shrink-0 overflow-hidden bg-gradient-to-br from-primary/10 to-accent/5">
          {imgURL
            ? <img src={imgURL} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            : <div className="w-full h-full flex items-center justify-center"><ChefHat size={28} className="text-text-tertiary/30" /></div>
          }
        </div>
        <div className="p-md flex-1 min-w-0">
          <p className="text-callout font-semibold text-text-primary line-clamp-2 mb-xs">{recipe.title}</p>
          <div className="flex items-center gap-md text-caption text-text-secondary flex-wrap">
            {recipe.rating > 0 && <span className="flex items-center gap-xs"><Star size={11} className="text-accent fill-accent" />{Number(recipe.rating).toFixed(1)}</span>}
            {time > 0 && <span className="flex items-center gap-xs"><Clock size={11} />{time}m</span>}
            {recipe.servings > 0 && <span className="flex items-center gap-xs"><Users size={11} />{recipe.servings}</span>}
            {recipe.difficulty && <span className="capitalize">{recipe.difficulty}</span>}
          </div>
          {recipe.authorName && <p className="text-caption text-text-tertiary mt-xs">by {recipe.authorName}</p>}
        </div>
      </div>
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// SectionsEditor — add/reorder/delete heading, subtitle, paragraph, recipe blocks
// ---------------------------------------------------------------------------

const SectionsEditor = ({ sections, onSave, cookbookId, allRecipes, loadingAll }) => {
  const [local,       setLocal]       = useState(sections || []);
  const [addType,     setAddType]     = useState(null);    // which type is being added
  const [addText,     setAddText]     = useState('');
  const [addRecipeId, setAddRecipeId] = useState('');
  const [recipeSearch, setRecipeSearch] = useState('');
  const [saving,      setSaving]      = useState(false);

  const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const addSection = () => {
    if (addType === SECTION_TYPES.recipe) {
      if (!addRecipeId) return;
      setLocal(prev => [...prev, { id: uid(), type: SECTION_TYPES.recipe, recipeId: addRecipeId }]);
      setAddRecipeId('');
    } else {
      if (!addText.trim()) return;
      setLocal(prev => [...prev, { id: uid(), type: addType, text: addText.trim() }]);
      setAddText('');
    }
    setAddType(null);
    setRecipeSearch('');
  };

  const removeSection = (id) => setLocal(prev => prev.filter(s => s.id !== id));

  const moveSection = (index, dir) => {
    setLocal(prev => {
      const next   = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(local);
    setSaving(false);
  };

  const filteredRecipes = allRecipes.filter(r => {
    const inSections = local.some(s => s.type === SECTION_TYPES.recipe && s.recipeId === r.firestoreId);
    if (inSections) return false;
    if (!recipeSearch.trim()) return true;
    return (r.title || '').toLowerCase().includes(recipeSearch.toLowerCase());
  });

  const ADD_BUTTONS = [
    { type: SECTION_TYPES.heading,   label: 'Heading',   icon: Heading1    },
    { type: SECTION_TYPES.subtitle,  label: 'Subtitle',  icon: Heading2    },
    { type: SECTION_TYPES.paragraph, label: 'Paragraph', icon: AlignLeft   },
    { type: SECTION_TYPES.recipe,    label: 'Recipe',    icon: BookMarked  },
  ];

  return (
    <div className="space-y-lg">

      {/* Existing blocks */}
      {local.length === 0 ? (
        <div
          className="rounded-large p-xl text-center"
          style={{ border: '2px dashed rgba(var(--color-text-tertiary),0.15)' }}
        >
          <Type size={32} className="text-text-tertiary/30 mx-auto mb-md" />
          <p className="text-callout font-semibold text-text-secondary mb-xs">No content yet</p>
          <p className="text-caption text-text-tertiary">Add headings, text, or embedded recipes below.</p>
        </div>
      ) : (
        <div className="space-y-sm">
          {local.map((section, index) => (
            <div
              key={section.id}
              className="flex items-start gap-sm p-md rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              {/* Type pill */}
              <span
                className="flex-shrink-0 mt-xs px-sm py-xs rounded-full text-caption font-semibold capitalize"
                style={{ background: 'rgba(var(--color-primary-rgb),0.08)', color: 'var(--color-primary)' }}
              >
                {section.type === 'text' ? 'paragraph' : section.type}
              </span>

              {/* Preview */}
              <div className="flex-1 min-w-0 mt-xs">
                {section.type === SECTION_TYPES.recipe
                  ? <p className="text-caption text-text-secondary truncate">Recipe ID: {section.recipeId}</p>
                  : <p className="text-body text-text-primary truncate">{section.text}</p>
                }
              </div>

              {/* Controls */}
              <div className="flex items-center gap-xs flex-shrink-0">
                <button
                  onClick={() => moveSection(index, -1)}
                  disabled={index === 0}
                  className="w-7 h-7 rounded-medium bg-surface border border-text-tertiary/15 flex items-center justify-center text-text-tertiary hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30"
                >
                  <ChevronUp size={13} />
                </button>
                <button
                  onClick={() => moveSection(index, 1)}
                  disabled={index === local.length - 1}
                  className="w-7 h-7 rounded-medium bg-surface border border-text-tertiary/15 flex items-center justify-center text-text-tertiary hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30"
                >
                  <ChevronDown size={13} />
                </button>
                <button
                  onClick={() => removeSection(section.id)}
                  className="w-7 h-7 rounded-full bg-error/10 hover:bg-error/20 flex items-center justify-center transition-colors"
                >
                  <Trash2 size={13} className="text-error" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add block UI */}
      {addType === null ? (
        <div>
          <p className="text-caption font-semibold text-text-tertiary uppercase tracking-wide mb-sm">Add block</p>
          <div className="flex flex-wrap gap-sm">
            {ADD_BUTTONS.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                onClick={() => setAddType(type)}
                className="flex items-center gap-xs px-md py-sm rounded-large text-caption font-semibold transition-all"
                style={{ background: 'rgba(var(--color-primary-rgb),0.06)', border: '1px dashed rgba(var(--color-primary-rgb),0.2)', color: 'var(--color-primary)' }}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : addType === SECTION_TYPES.recipe ? (
        <div
          className="rounded-large p-md space-y-md"
          style={{ background: 'rgba(var(--color-surface),1)', border: '1px solid rgba(var(--color-text-tertiary),0.12)' }}
        >
          <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide">Add Recipe Block</p>
          <div className="relative">
            <Search size={14} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              value={recipeSearch}
              onChange={e => setRecipeSearch(e.target.value)}
              placeholder="Search recipes..."
              autoFocus
              className="w-full pl-xxxl pr-md py-sm bg-white border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body text-text-primary"
            />
          </div>
          {loadingAll ? (
            <div className="text-center py-md"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" /></div>
          ) : (
            <div className="max-h-60 overflow-y-auto space-y-xs">
              {filteredRecipes.slice(0, 20).map(recipe => {
                const imgURL = getRecipeImageURL(recipe);
                const sel    = addRecipeId === recipe.firestoreId;
                return (
                  <button
                    key={recipe.firestoreId}
                    onClick={() => setAddRecipeId(sel ? '' : recipe.firestoreId)}
                    className={`w-full flex items-center gap-md p-md rounded-medium border transition-all text-left ${
                      sel ? 'border-primary/30 bg-primary/5' : 'border-text-tertiary/10 hover:border-text-tertiary/20'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'border-primary bg-primary' : 'border-text-tertiary/30'}`}>
                      {sel && <Check size={11} className="text-white" />}
                    </div>
                    <div className="w-10 h-10 rounded-medium overflow-hidden bg-surface flex-shrink-0">
                      {imgURL ? <img src={imgURL} alt={recipe.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ChefHat size={16} className="text-text-tertiary" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-callout font-semibold text-text-primary truncate">{recipe.title}</p>
                      {recipe.authorName && <p className="text-caption text-text-tertiary">by {recipe.authorName}</p>}
                    </div>
                  </button>
                );
              })}
              {filteredRecipes.length === 0 && <p className="text-center text-caption text-text-secondary py-md">{recipeSearch ? `No results for "${recipeSearch}"` : 'No recipes available'}</p>}
            </div>
          )}
          <div className="flex gap-sm">
            <Button variant="outline" size="small" onClick={() => { setAddType(null); setAddRecipeId(''); setRecipeSearch(''); }} className="flex-1">Cancel</Button>
            <Button variant="primary" size="small" onClick={addSection} disabled={!addRecipeId} className="flex-1">Add Recipe</Button>
          </div>
        </div>
      ) : (
        <div
          className="rounded-large p-md space-y-md"
          style={{ background: 'rgba(var(--color-surface),1)', border: '1px solid rgba(var(--color-text-tertiary),0.12)' }}
        >
          <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide">
            Add {addType.charAt(0).toUpperCase() + addType.slice(1)}
          </p>
          {addType === SECTION_TYPES.paragraph ? (
            <textarea
              value={addText}
              onChange={e => setAddText(e.target.value)}
              placeholder="Write your text..."
              rows={4}
              autoFocus
              className="w-full px-md py-sm bg-white border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none resize-none text-body text-text-primary"
            />
          ) : (
            <input
              type="text"
              value={addText}
              onChange={e => setAddText(e.target.value)}
              placeholder={addType === SECTION_TYPES.heading ? 'Heading text...' : 'Subtitle text...'}
              autoFocus
              className="w-full px-md py-sm bg-white border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body text-text-primary"
            />
          )}
          <div className="flex gap-sm">
            <Button variant="outline" size="small" onClick={() => { setAddType(null); setAddText(''); }} className="flex-1">Cancel</Button>
            <Button variant="primary" size="small" onClick={addSection} disabled={!addText.trim()} className="flex-1">Add</Button>
          </div>
        </div>
      )}

      {/* Save */}
      <Button variant="primary" onClick={handleSave} disabled={saving} className="w-full">
        {saving ? 'Saving...' : 'Save Content'}
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CoverImageUpload — reuses existing uploadImageToR2
// ---------------------------------------------------------------------------

const CoverImageUpload = ({ cookbookId, currentURL, onUploaded }) => {
  const inputRef              = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadImageToR2(file, 'cookbook', cookbookId, 'cover');
      onUploaded(result.urls.medium);
    } catch (err) {
      console.error('Cover image upload error:', err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      {currentURL ? (
        <div className="relative rounded-large overflow-hidden">
          <img src={currentURL} alt="Cover" className="w-full h-40 object-cover" />
          <div className="absolute top-sm right-sm flex gap-xs">
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-xs px-sm py-xs bg-black/60 rounded-medium text-white text-xs font-semibold hover:bg-black/80 transition-all disabled:opacity-50"
            >
              <Upload size={12} />
              {uploading ? 'Uploading...' : 'Change'}
            </button>
            <button
              onClick={() => onUploaded(null)}
              className="w-7 h-7 rounded-full bg-error flex items-center justify-center text-white hover:bg-error/80 transition-all"
            >
              <X size={13} />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full h-32 border-2 border-dashed rounded-large flex flex-col items-center justify-center hover:border-primary transition-colors disabled:opacity-50"
          style={{ borderColor: 'rgba(var(--color-primary-rgb),0.25)', background: 'rgba(var(--color-primary-rgb),0.03)' }}
        >
          {uploading ? (
            <><div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin mb-sm" /><p className="text-caption text-text-secondary">Uploading...</p></>
          ) : (
            <><ImageIcon size={28} className="text-text-tertiary mb-sm" style={{ opacity: 0.4 }} /><p className="text-callout text-text-secondary">Upload cover photo</p><p className="text-caption text-text-tertiary mt-xs">PNG, JPG up to 10MB</p></>
          )}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CollectionDetailView
// ---------------------------------------------------------------------------

const CollectionDetailView = ({ collectionData, onBack, onNavigateToRecipe, onCreateRecipe }) => {
  const { addRecipeToCookbook, removeRecipeFromCookbook, deleteCookbook, updateSections, updateCookbook, updateCoverImage } = useCookbooks();
  const { careProfiles } = useCareProfiles();

  const [recipes,        setRecipes]        = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [showAddPanel,   setShowAddPanel]   = useState(false);
  const [allRecipes,     setAllRecipes]     = useState([]);
  const [loadingAll,     setLoadingAll]     = useState(false);
  const [addSearch,      setAddSearch]      = useState('');
  const [selectedToAdd,  setSelectedToAdd]  = useState(new Set());
  const [adding,         setAdding]         = useState(false);

  // Active tab: 'recipes' | 'content' | 'settings'
  const [activeTab, setActiveTab] = useState('recipes');

  const [showEditModal,      setShowEditModal]      = useState(false);
  const [editName,           setEditName]           = useState(collectionData.name);
  const [editDesc,           setEditDesc]           = useState(collectionData.description || '');
  const [editStatus,         setEditStatus]         = useState(collectionData.status || (collectionData.isPublic ? 'published' : 'private'));
  const [editCareProfileIds, setEditCareProfileIds] = useState(collectionData.careProfileIds || []);
  const [editDietaryTags,    setEditDietaryTags]    = useState(collectionData.dietaryTags || []);
  const [saving,             setSaving]             = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting,          setDeleting]          = useState(false);

  const [localRecipeIds, setLocalRecipeIds] = useState(collectionData.recipeIds || []);
  const [localSections,  setLocalSections]  = useState(collectionData.sections  || []);
  const [localCoverURL,  setLocalCoverURL]  = useState(collectionData.coverImageURL || null);

  // Build a map of recipeId → recipe for section rendering
  const recipeMap = Object.fromEntries(recipes.map(r => [r.firestoreId, r]));

  const loadRecipes = useCallback(async (ids) => {
    if (!ids?.length) { setRecipes([]); return; }
    setLoadingRecipes(true);
    try {
      const batches = [];
      for (let i = 0; i < ids.length; i += 10) batches.push(ids.slice(i, i + 10));
      const loaded = [];
      for (const batch of batches) {
        const snap = await getDocs(query(collection(db, 'recipes'), where('__name__', 'in', batch)));
        snap.docs.forEach(d => loaded.push({ ...d.data(), firestoreId: d.id }));
      }
      setRecipes(loaded);
    } catch (err) { console.error(err); }
    finally { setLoadingRecipes(false); }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRecipes(localRecipeIds); }, []);

  const loadAllRecipes = async () => {
    if (allRecipes.length) return;
    setLoadingAll(true);
    try {
      const snap = await getDocs(query(collection(db, 'recipes'), orderBy('createdAt', 'desc')));
      setAllRecipes(snap.docs.map(d => ({ ...d.data(), firestoreId: d.id })).filter(r => r.status === 'approved' && r.isVisible));
    } catch (err) { console.error(err); }
    finally { setLoadingAll(false); }
  };

  const handleAddSelected = async () => {
    if (!selectedToAdd.size) return;
    setAdding(true);
    try {
      for (const id of selectedToAdd) await addRecipeToCookbook(collectionData.id, id);
      const newIds = [...new Set([...localRecipeIds, ...selectedToAdd])];
      setLocalRecipeIds(newIds);
      await loadRecipes(newIds);
      setShowAddPanel(false);
      setSelectedToAdd(new Set());
    } catch (err) { console.error(err); }
    finally { setAdding(false); }
  };

  const handleRemove = async (recipeId) => {
    await removeRecipeFromCookbook(collectionData.id, recipeId);
    setLocalRecipeIds(prev => prev.filter(id => id !== recipeId));
    setRecipes(prev => prev.filter(r => r.firestoreId !== recipeId));
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await updateCookbook(collectionData.id, {
        name:           editName.trim(),
        description:    editDesc.trim(),
        status:         editStatus,
        dietaryTags:    editDietaryTags,
        careProfileIds: editCareProfileIds,
      });
      Object.assign(collectionData, {
        name:           editName.trim(),
        description:    editDesc.trim(),
        status:         editStatus,
        isPublic:       editStatus === 'published',
        dietaryTags:    editDietaryTags,
        careProfileIds: editCareProfileIds,
      });
      setShowEditModal(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleSaveSections = async (sections) => {
    await updateSections(collectionData.id, sections);
    setLocalSections(sections);
  };

  const handleCoverUploaded = async (url) => {
    await updateCoverImage(collectionData.id, url);
    setLocalCoverURL(url);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteCookbook(collectionData.id); onBack(); }
    catch (err) { console.error(err); setDeleting(false); }
  };

  const tagged       = careProfiles.filter(p => (collectionData.careProfileIds || []).includes(p.id));
  const filteredAdd  = allRecipes.filter(r => {
    if (localRecipeIds.includes(r.firestoreId)) return false;
    if (!addSearch.trim()) return true;
    return (r.title || '').toLowerCase().includes(addSearch.toLowerCase());
  });

  const TABS = [
    { id: 'recipes', label: 'Recipes' },
    { id: 'content', label: 'Content' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="space-y-lg">

      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-sm text-text-secondary hover:text-primary transition-colors">
          <ArrowLeft size={20} /><span className="text-callout font-semibold">Back</span>
        </button>
        <div className="flex gap-sm">
          <button onClick={() => setShowEditModal(true)} className="w-9 h-9 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:border-primary/40 transition-colors">
            <Edit2 size={15} className="text-text-secondary" />
          </button>
          <button onClick={() => setShowDeleteConfirm(true)} className="w-9 h-9 rounded-full bg-surface border border-text-tertiary/15 flex items-center justify-center hover:border-error/40 transition-colors">
            <Trash2 size={15} className="text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Collection header */}
      <div className="flex items-start gap-lg">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-sm flex-wrap">
            <h1 className="text-display font-rounded text-primary">{collectionData.name}</h1>
            <span className={`px-sm py-xs rounded-full text-caption font-semibold flex items-center gap-xs ${collectionData.isPublic ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`}>
              {collectionData.isPublic ? <Globe size={11} /> : <Lock size={11} />}
              {collectionData.isPublic ? 'Public' : 'Private'}
            </span>
          </div>
          {collectionData.description && <p className="text-body text-text-secondary mt-xs">{collectionData.description}</p>}
          <p className="text-caption text-text-tertiary mt-sm">{localRecipeIds.length} recipe{localRecipeIds.length !== 1 ? 's' : ''}</p>
        </div>
        {tagged.length > 0 && (
          <div className="flex items-center gap-sm flex-shrink-0">
            <span className="text-caption text-text-tertiary">For</span>
            <div className="flex -space-x-1.5">{tagged.slice(0, 5).map(p => <ProfileChip key={p.id} profile={p} size="md" />)}</div>
          </div>
        )}
      </div>

      {/* Tab picker */}
      <div
        className="flex rounded-large p-xs"
        style={{ background: 'rgba(var(--color-primary-rgb),0.06)', border: '1px solid rgba(var(--color-primary-rgb),0.1)' }}
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-sm rounded-large text-caption font-semibold transition-all ${
              activeTab === tab.id ? 'bg-primary text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── RECIPES TAB ── */}
      {activeTab === 'recipes' && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-title-3 font-semibold text-text-primary">Recipes</h2>
            <div className="flex gap-sm">
              {onCreateRecipe && (
                <Button variant="outline" size="small" onClick={onCreateRecipe}><Plus size={14} className="mr-xs" />Create</Button>
              )}
              <Button variant="primary" size="small" onClick={() => { setShowAddPanel(true); loadAllRecipes(); }}><Plus size={14} className="mr-xs" />Add</Button>
            </div>
          </div>

          {loadingRecipes ? (
            <div className="text-center py-xl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /></div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-xxxl border-2 border-dashed border-text-tertiary/20 rounded-large">
              <BookOpen size={40} className="text-text-tertiary/30 mx-auto mb-md" />
              <p className="text-callout font-semibold text-text-secondary mb-lg">No recipes yet</p>
              <Button variant="primary" onClick={() => { setShowAddPanel(true); loadAllRecipes(); }}><Plus size={16} className="mr-xs" />Add Recipes</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-md">
              {recipes.map(recipe => {
                const imgURL = getRecipeImageURL(recipe);
                const id     = recipe.firestoreId;
                const time   = recipe.totalTimeMinutes || ((recipe.prepTimeMinutes || 0) + (recipe.cookTimeMinutes || 0)) || null;
                return (
                  <div key={id} className="relative group">
                    <Card className="hover:shadow-md transition-all cursor-pointer p-0 overflow-hidden" onClick={() => onNavigateToRecipe?.(recipe)}>
                      <div className="h-44 relative overflow-hidden bg-gradient-to-br from-accent/10 to-primary/5">
                        {imgURL
                          ? <img src={imgURL} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          : <div className="w-full h-full flex items-center justify-center"><ChefHat size={40} className="text-text-tertiary/30" /></div>
                        }
                        <button
                          onClick={e => { e.stopPropagation(); handleRemove(id); }}
                          className="absolute top-sm right-sm w-7 h-7 rounded-full bg-error flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all shadow"
                        ><X size={13} /></button>
                      </div>
                      <div className="p-md">
                        <h4 className="text-callout font-semibold text-text-primary line-clamp-2 mb-xs">{recipe.title}</h4>
                        <div className="flex items-center gap-md text-caption text-text-secondary">
                          {recipe.rating > 0 && <span className="flex items-center gap-xs"><Star size={10} className="text-accent fill-accent" />{Number(recipe.rating).toFixed(1)}</span>}
                          {time > 0 && <span className="flex items-center gap-xs"><Clock size={10} />{time}m</span>}
                          {recipe.servings > 0 && <span className="flex items-center gap-xs"><Users size={10} />{recipe.servings}</span>}
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── CONTENT TAB ── */}
      {activeTab === 'content' && (
        <div className="space-y-lg">
          {/* Cover image */}
          <div>
            <p className="text-callout font-semibold text-text-primary mb-md">Cover Photo</p>
            <CoverImageUpload
              cookbookId={collectionData.id}
              currentURL={localCoverURL}
              onUploaded={handleCoverUploaded}
            />
          </div>

          {/* Sections editor */}
          <div>
            <p className="text-callout font-semibold text-text-primary mb-xs">Body Content</p>
            <p className="text-caption text-text-secondary mb-md">
              Build a blog-style body with headings, text, and embedded recipe cards.
            </p>
            <SectionsEditor
              sections={localSections}
              onSave={handleSaveSections}
              cookbookId={collectionData.id}
              allRecipes={allRecipes.length ? allRecipes : []}
              loadingAll={loadingAll}
            />
          </div>

          {/* Read-only preview of saved sections */}
          {localSections.length > 0 && (
            <div>
              <p className="text-callout font-semibold text-text-primary mb-md">Preview</p>
              <div
                className="rounded-large p-xl space-y-lg"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
              >
                {localSections.map(section => (
                  <SectionBlock
                    key={section.id}
                    section={section}
                    recipeMap={recipeMap}
                    onClickRecipe={onNavigateToRecipe}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SETTINGS TAB ── */}
      {activeTab === 'settings' && (
        <div className="space-y-lg">
          <div
            className="rounded-large p-lg space-y-lg"
            style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
          >
            <div className="space-y-sm">
              <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Name</label>
              <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body" />
            </div>
            <div className="space-y-sm">
              <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Description</label>
              <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={3} maxLength={200} className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none" />
            </div>
            <div className="space-y-sm">
              <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Visibility</label>
              <StatusSelector value={editStatus} onChange={setEditStatus} />
            </div>
            <div className="space-y-sm">
              <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block flex items-center gap-xs">
                <Leaf size={13} className="text-primary" />Dietary Tags
              </label>
              <div className="flex flex-wrap gap-sm">
                {DIETARY_TAGS.map(tag => {
                  const sel = editDietaryTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => setEditDietaryTags(prev => sel ? prev.filter(t => t !== tag) : [...prev, tag])}
                      className={`px-md py-xs rounded-full text-caption font-semibold transition-all ${
                        sel ? 'bg-primary text-white' : 'bg-surface border border-text-tertiary/30 text-text-secondary hover:border-primary/30'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
            {careProfiles.length > 0 && (
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">
                  For Care Profiles <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-sm">
                  {careProfiles.map(p => {
                    const sel = editCareProfileIds.includes(p.id);
                    return (
                      <button key={p.id}
                        onClick={() => setEditCareProfileIds(prev => sel ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                        className={`flex items-center gap-sm px-md py-sm rounded-full border text-caption font-semibold transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-text-tertiary/20 text-text-secondary hover:border-primary/30'}`}
                      >
                        <ProfileChip profile={p} size="sm" />{p.name}{sel && <Check size={12} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-sm">
            <Button variant="outline" onClick={onBack} className="flex-1">Cancel</Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={!editName.trim() || saving} className="flex-1">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Add Recipes Panel ── */}
      {showAddPanel && (
        <Modal onBackdropClick={() => setShowAddPanel(false)}>
          <div className="bg-background rounded-large border border-text-tertiary/20 w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl">
            <div className="p-lg border-b border-text-tertiary/10 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-title-3 font-semibold text-primary">Add Recipes</h3>
                <p className="text-caption text-text-secondary mt-xs">{selectedToAdd.size > 0 ? `${selectedToAdd.size} selected` : 'Select recipes to add'}</p>
              </div>
              <button onClick={() => setShowAddPanel(false)} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center"><X size={18} className="text-text-tertiary" /></button>
            </div>
            <div className="p-lg border-b border-text-tertiary/10 flex-shrink-0">
              <div className="relative">
                <Search size={16} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" />
                <input type="text" value={addSearch} onChange={e => setAddSearch(e.target.value)} placeholder="Search recipes..." autoFocus className="w-full pl-xxxl pr-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-lg">
              {loadingAll ? (
                <div className="text-center py-xl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /></div>
              ) : filteredAdd.length === 0 ? (
                <p className="text-center text-caption text-text-secondary py-xl">{addSearch ? `No results for "${addSearch}"` : 'All recipes already added'}</p>
              ) : (
                <div className="space-y-sm">
                  {filteredAdd.map(recipe => {
                    const id     = recipe.firestoreId;
                    const sel    = selectedToAdd.has(id);
                    const imgURL = getRecipeImageURL(recipe);
                    return (
                      <button key={id}
                        onClick={() => setSelectedToAdd(prev => { const n = new Set(prev); sel ? n.delete(id) : n.add(id); return n; })}
                        className={`w-full flex items-center gap-md p-md rounded-medium border transition-all text-left ${sel ? 'border-primary/30 bg-primary/5' : 'border-text-tertiary/10 hover:border-text-tertiary/20'}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'border-primary bg-primary' : 'border-text-tertiary/30'}`}>
                          {sel && <Check size={11} className="text-white" />}
                        </div>
                        <div className="w-12 h-12 rounded-medium overflow-hidden bg-surface flex-shrink-0">
                          {imgURL ? <img src={imgURL} alt={recipe.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ChefHat size={18} className="text-text-tertiary" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-callout font-semibold text-text-primary truncate">{recipe.title}</p>
                          <p className="text-caption text-text-secondary mt-xs capitalize">{recipe.difficulty}{recipe.authorName ? ` · by ${recipe.authorName}` : ''}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-lg border-t border-text-tertiary/10 flex gap-md flex-shrink-0">
              <Button variant="outline" onClick={() => setShowAddPanel(false)} className="flex-1">Cancel</Button>
              <Button variant="primary" onClick={handleAddSelected} disabled={!selectedToAdd.size || adding} className="flex-1">
                {adding ? 'Adding...' : `Add ${selectedToAdd.size > 0 ? selectedToAdd.size + ' ' : ''}Recipe${selectedToAdd.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal (quick edit from top bar) ── */}
      {showEditModal && (
        <Modal onBackdropClick={() => setShowEditModal(false)}>
          <div className="bg-background rounded-large border border-text-tertiary/15 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-xl pt-xl pb-lg border-b border-text-tertiary/10">
              <h3 className="text-title-3 font-semibold text-text-primary">Edit Collection</h3>
              <button onClick={() => setShowEditModal(false)} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center"><X size={18} className="text-text-tertiary" /></button>
            </div>
            <div className="p-xl space-y-lg">
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Name</label>
                <input type="text" value={editName} onChange={e => setEditName(e.target.value)} autoFocus className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body" />
              </div>
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Description <span className="font-normal normal-case text-text-tertiary">(optional)</span></label>
                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2} maxLength={200} className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none" />
              </div>
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Visibility</label>
                <StatusSelector value={editStatus} onChange={setEditStatus} />
              </div>
            </div>
            <div className="flex gap-sm px-xl pb-xl">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Cancel</Button>
              <Button variant="primary" onClick={handleSaveEdit} disabled={!editName.trim() || saving} className="flex-1">{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm ── */}
      {showDeleteConfirm && (
        <Modal onBackdropClick={() => setShowDeleteConfirm(false)}>
          <Card className="max-w-sm w-full text-center">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-md"><Trash2 size={24} className="text-error" /></div>
            <h3 className="text-title-3 font-bold text-text-primary mb-sm">Delete Collection?</h3>
            <p className="text-body text-text-secondary mb-lg">"{collectionData.name}" will be deleted. Recipes won't be deleted.</p>
            <div className="flex gap-sm">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</Button>
              <Button variant="primary" className="flex-1 bg-error border-error hover:bg-error/90" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
            </div>
          </Card>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main CookbookHub
// ---------------------------------------------------------------------------

const CookbookHub = ({ onNavigateToRecipes, onViewCollection, onCreateRecipe, onNavigateToRecipe }) => {
  const { cookbooks, loading, createCookbook, deleteCookbook } = useCookbooks();
  const { careProfiles } = useCareProfiles();

  const [selectedCollection, setSelectedCollection] = useState(null);
  const [coverImages,         setCoverImages]        = useState({});

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName,         setNewName]         = useState('');
  const [newDesc,         setNewDesc]         = useState('');
  const [newStatus,       setNewStatus]       = useState('private');
  const [newCareIds,      setNewCareIds]      = useState([]);
  const [newDietaryTags,  setNewDietaryTags]  = useState([]);
  const [creating,        setCreating]        = useState(false);

  // Prefetch mosaic cover images
  useEffect(() => {
    if (!cookbooks.length) return;
    cookbooks.forEach(async (cb) => {
      if (coverImages[cb.id] !== undefined) return;
      const ids = (cb.recipeIds || []).slice(0, 4);
      if (!ids.length) { setCoverImages(prev => ({ ...prev, [cb.id]: [] })); return; }
      try {
        const snap = await getDocs(query(collection(db, 'recipes'), where('__name__', 'in', ids)));
        const urls = snap.docs
          .map(d => { const data = d.data(); return data.imageURL || data.imageUrl || data.images?.[0]?.urls?.medium || null; })
          .filter(Boolean);
        setCoverImages(prev => ({ ...prev, [cb.id]: urls }));
      } catch {
        setCoverImages(prev => ({ ...prev, [cb.id]: [] }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookbooks]);

  const resetModal = () => {
    setNewName(''); setNewDesc(''); setNewStatus('private');
    setNewCareIds([]); setNewDietaryTags([]); setShowCreateModal(false);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createCookbook(
        newName.trim(), newDesc.trim(),
        newStatus === 'published', newCareIds,
        null, newStatus, newDietaryTags, [],
      );
      resetModal();
    } catch { alert('Failed to create collection'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this collection? Recipes will not be deleted.')) return;
    try { await deleteCookbook(id); } catch { alert('Failed to delete'); }
  };

  if (selectedCollection) {
    const current = cookbooks.find(c => c.id === selectedCollection.id) || selectedCollection;
    return (
      <CollectionDetailView
        collectionData={current}
        onBack={() => setSelectedCollection(null)}
        onNavigateToRecipe={onNavigateToRecipe}
        onCreateRecipe={onCreateRecipe}
      />
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-xxxl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-xl">

      {/* Header */}
      <div className="flex items-end justify-between gap-md flex-wrap">
        <div>
          <h1 className="text-display font-rounded text-primary">My Cookbooks</h1>
          <p className="text-body text-text-secondary mt-xs">Your recipes and collections, all in one place.</p>
        </div>
        <div className="flex gap-sm">
          <Button variant="outline" onClick={onCreateRecipe}><Plus size={16} className="mr-xs" />New Recipe</Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}><FolderPlus size={16} className="mr-xs" />New Collection</Button>
        </div>
      </div>

      {/* Book grid */}
      {cookbooks.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-xl pt-sm">
          {cookbooks.map(col => (
            <CookbookCard
              key={col.id}
              cookbook={col}
              recipeImages={coverImages[col.id] || []}
              careProfiles={careProfiles}
              onClick={() => setSelectedCollection(col)}
              onDelete={(e) => handleDelete(e, col.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-xxxl border-2 border-dashed border-text-tertiary/15 rounded-large">
          <BookOpen size={40} className="text-text-tertiary/25 mx-auto mb-md" />
          <p className="text-callout font-semibold text-text-secondary mb-xs">No collections yet</p>
          <p className="text-caption text-text-tertiary mb-lg">Create one to organise your favourite recipes.</p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}><FolderPlus size={16} className="mr-xs" />Create Collection</Button>
        </div>
      )}

      {/* ── Create Modal ── */}
      {showCreateModal && (
        <Modal onBackdropClick={resetModal}>
          <div className="bg-background rounded-large border border-text-tertiary/15 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-xl pt-xl pb-lg border-b border-text-tertiary/10">
              <h3 className="text-title-3 font-semibold text-text-primary">New Collection</h3>
              <button onClick={resetModal} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center transition-colors"><X size={18} className="text-text-tertiary" /></button>
            </div>

            <div className="p-xl space-y-lg">
              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Name</label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Weeknight Dinners" autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body placeholder:text-text-tertiary/50" />
              </div>

              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">
                  Description <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                </label>
                <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="What makes this collection special?" rows={2} maxLength={200}
                  className="w-full px-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body resize-none placeholder:text-text-tertiary/50" />
              </div>

              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">Visibility</label>
                <StatusSelector value={newStatus} onChange={setNewStatus} />
              </div>

              <div className="space-y-sm">
                <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block flex items-center gap-xs">
                  <Leaf size={13} className="text-primary" />Dietary Tags <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-sm">
                  {DIETARY_TAGS.map(tag => {
                    const sel = newDietaryTags.includes(tag);
                    return (
                      <button key={tag}
                        onClick={() => setNewDietaryTags(prev => sel ? prev.filter(t => t !== tag) : [...prev, tag])}
                        className={`px-md py-xs rounded-full text-caption font-semibold transition-all ${
                          sel ? 'bg-primary text-white' : 'bg-surface border border-text-tertiary/30 text-text-secondary hover:border-primary/30'
                        }`}
                      >
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>

              {careProfiles.length > 0 && (
                <div className="space-y-sm">
                  <label className="text-caption font-semibold text-text-secondary uppercase tracking-wider block">
                    For Care Profiles <span className="font-normal normal-case text-text-tertiary">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-sm">
                    {careProfiles.map(p => {
                      const sel = newCareIds.includes(p.id);
                      return (
                        <button key={p.id}
                          onClick={() => setNewCareIds(prev => sel ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                          className={`flex items-center gap-sm px-md py-sm rounded-full border text-caption font-semibold transition-all ${sel ? 'border-primary bg-primary/10 text-primary' : 'border-text-tertiary/20 text-text-secondary hover:border-primary/30'}`}>
                          <ProfileChip profile={p} size="sm" />{p.name}{sel && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-sm px-xl pb-xl">
              <Button variant="outline" onClick={resetModal} className="flex-1">Cancel</Button>
              <Button variant="primary" onClick={handleCreate} disabled={!newName.trim() || creating} className="flex-1">
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CookbookHub;